import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { OAuthService, JwksValidationHandler } from 'angular-oauth2-oidc';
import { SettingsService } from '../../settings/services/settings.service';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/observable/fromPromise';

@Injectable()
export class AuthService {
  private appSettings = this.settingsService.Get();
  private userLoggedIn: boolean;

  get isLoggedIn() {
    return this.userLoggedIn;
  }

  onLoggedIn = new Subject<boolean>();

  constructor(
    private oAuthService: OAuthService,
    private settingsService: SettingsService
  ) {
    if (!this.appSettings.useAuthentication) {
      return;
    }

    this.oAuthService.configure(this.getAuthServiceConfiguration());
    this.oAuthService.events.subscribe(e => {
      this.verifySilentRefreshSuccess(e);
      this.verifyTokenIsValid(e);
    });
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    this.oAuthService.setupAutomaticSilentRefresh();
    this.oAuthService.loadDiscoveryDocument().then(
      success => {
        this.oAuthService.tryLogin().then(
          () => {
            this.verifyLoginState();
          },
          error => {
            console.log(error);
          }
        );
      },
      loadError => {
        console.log(loadError);
      }
    );
  }

  private getAuthServiceConfiguration() {
    return {
      clientId: this.appSettings.clientId,
      issuer: this.appSettings.identityUrl,
      redirectUri: this.appSettings.redirectUri,
      scope: this.appSettings.scope,
      logoutUrl: `${this.appSettings.identityUrl}/connect/endsession`,
      userinfoEndpoint: `${this.appSettings.identityUrl}/connect/userinfo`,
      requireHttps: false,
      strictDiscoveryDocumentValidation: true,
      silentRefreshRedirectUri: window.location.origin + '/silent-refresh.html',
      timeoutFactor: 0.7
    };
  }

  accessTokenIsValid() {
    return this.oAuthService.hasValidAccessToken();
  }

  refreshUserLogin() {
    if (!this.userLoggedIn) {
      return this.onLoggedIn.map(response => {
        return response;
      });
    } else if (this.userLoggedIn && !this.oAuthService.hasValidAccessToken()) {
      return Observable.fromPromise(
        this.oAuthService.silentRefresh()
      ).map(response => {
        if (response.type === 'silently_refreshed') {
          return true;
        }
        return false;
      });
    } else {
      return Observable.of(true);
    }
  }

  private verifyLoginState() {
    const token = this.oAuthService.getAccessToken();
    if (token != null) {
      this.emitNewLogInResult(true);
    } else {
      this.oAuthService.initImplicitFlow();
    }
  }

  private verifySilentRefreshSuccess(event: any) {
    if (
      event.type === 'token_error' &&
      event.params.error === 'login_required'
    ) {
      this.oAuthService.initImplicitFlow();
    }
  }

  private verifyTokenIsValid(event: any) {
    if (event.type === 'token_error' || event.type === 'invalid_nonce_in_state') {
      console.warn(event);
      this.oAuthService.initImplicitFlow();
    }
  }
  
  getAuthorisationHeader() {
    try {
      const tokenIsValid = this.accessTokenIsValid();
      const token = this.getAccessToken();

      if (token !== null && tokenIsValid) {
        const headers = new HttpHeaders({
          Authorization: 'Bearer ' + token
        });
        return headers;
      }
      this.oAuthService.initImplicitFlow();
      return null;
    } catch (error) {
      console.log(error);
      this.oAuthService.initImplicitFlow();
      return null;
    }
  }

  getuserIsLoggedIn(): Observable<any> {
    return Observable.of(this.isLoggedIn);
  }

  getuserLoggedIn(): Observable<any> {
    return this.onLoggedIn;
  }

  tryLogin() {
    if (!this.appSettings.useAuthentication) {
      this.emitNewLogInResult(true);
      return;
    }
    this.oAuthService
      .tryLogin()
      .then(() => {
        if (this.oAuthService.getAccessToken() != null) {
          this.emitNewLogInResult(true);
        } else {
          this.emitNewLogInResult(false);
        }
      })
      .catch(() => {
        this.emitNewLogInResult(false);
      });
  }

  logout() {
    if (!this.appSettings.useAuthentication) {
      return;
    }
    this.oAuthService.logOut();
  }

  private getAccessToken() {
    if (!this.appSettings.useAuthentication) {
      return 'TOKEN';
    }

    return this.oAuthService.getAccessToken();
  }

  getUserLoginState() {
    this.onLoggedIn.next(this.userLoggedIn);
    return;
  }

  private emitNewLogInResult(loggedIn: boolean) {
    this.userLoggedIn = loggedIn;
    this.onLoggedIn.next(this.userLoggedIn);
  }
}



// WEBPACK FOOTER //
// ./src/app/auth/services/auth.service.ts